import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, fmtDate, download } from "../lib/api";
import { PageHeader, Section } from "../components/common";
import { ScheduleEditor } from "../components/CampaignForms";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { CheckCircle2, XCircle, AlertTriangle, Copy, ArrowUp, ArrowDown, Trash2, OctagonPause, Play } from "lucide-react";

function SecretField({ name, label, status, onSaved, placeholder }) {
  const [v, setV] = useState("");
  const save = async (val) => {
    try { await api.put("/secrets", { name, value: val }); toast.success(val ? "Clé enregistrée (chiffrée)" : "Clé supprimée"); setV(""); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label} {status?.set ? <span className="text-emerald-700 ml-1">· configurée {status.hint && <span className="font-mono">({status.hint})</span>}</span> : <span className="text-slate-400 ml-1">· non configurée</span>}</Label>
      <div className="flex gap-2">
        <Input type="password" autoComplete="new-password" value={v} placeholder={placeholder || (status?.set ? "Saisir pour remplacer" : "Coller la clé")} onChange={(e) => setV(e.target.value)} data-testid={`secret-${name}`} />
        <Button variant="outline" disabled={!v} onClick={() => save(v)} data-testid={`secret-save-${name}`}>Enregistrer</Button>
        {status?.set && <Button variant="ghost" onClick={() => save("")} data-testid={`secret-clear-${name}`}><Trash2 className="h-4 w-4" /></Button>}
      </div>
    </div>
  );
}

function TestButton({ name, label = "Tester la connexion" }) {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => { setBusy(true); try { const r = await api.post(`/integrations/${name}/test`); setRes(r.data); } catch (e) { setRes({ ok: false, error: errMsg(e) }); } setBusy(false); };
  return (
    <div className="space-y-2">
      <Button size="sm" variant="outline" onClick={run} disabled={busy} data-testid={`test-${name}`}>{busy ? "Test…" : label}</Button>
      {res && <div className={`text-xs rounded px-3 py-2 ${res.ok ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"}`} data-testid={`test-result-${name}`}>
        {res.ok ? <>Connexion réussie {res.details && Object.keys(res.details).length > 0 && <span className="font-mono">· {Object.entries(res.details).slice(0, 5).map(([k, v]) => `${k}: ${v}`).join(" · ")}</span>}</> : res.error}
      </div>}
    </div>
  );
}

function Integrations({ s, reload }) {
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Section title="Apollo.io" description="Recherche prioritaire des décideurs (People API Search + People Enrichment). Clé : Apollo → Settings → Integrations → API.">
        <div className="space-y-4"><SecretField name="apollo_api_key" label="Clé API Apollo (master key recommandée)" status={s.secrets.apollo_api_key} onSaved={reload} /><TestButton name="apollo" /></div>
      </Section>
      <Section title="RocketReach" description="Recherche complémentaire si Apollo ne fournit pas de contact exploitable. Clé : RocketReach → Account → API.">
        <div className="space-y-4"><SecretField name="rocketreach_api_key" label="Clé API RocketReach" status={s.secrets.rocketreach_api_key} onSaved={reload} /><TestButton name="rocketreach" /></div>
      </Section>
      <Section title="ZeroBounce (optionnel)" description="Validation complémentaire des emails non confirmés par Apollo / RocketReach. ~0,008 $ par vérification.">
        <div className="space-y-4"><SecretField name="zerobounce_api_key" label="Clé API ZeroBounce" status={s.secrets.zerobounce_api_key} onSaved={reload} /><TestButton name="zerobounce" /></div>
      </Section>
      <Section title="IA (Claude Sonnet 4.5)" description="Classification des réponses, personnalisation et brouillons. Fournie via la clé universelle Emergent — aucune clé à saisir.">
        <TestButton name="ai" label="Tester l'IA" />
      </Section>
    </div>
  );
}

function Microsoft({ s, reload }) {
  const [to, setTo] = useState("");
  const connected = s.microsoft.connected;
  const connect = async () => {
    try {
      const r = await api.get("/microsoft/auth-url");
      const w = window.open(r.data.url, "_blank");
      if (!w) { toast.error("Fenêtre bloquée : autorisez les fenêtres pop-up pour ce site, puis réessayez."); return; }
      toast.info("Terminez la connexion dans le nouvel onglet Microsoft…");
      let n = 0;
      const t = setInterval(async () => {
        n += 1;
        const s2 = await api.get("/settings").catch(() => null);
        if (s2?.data?.microsoft?.connected) { clearInterval(t); toast.success("Microsoft 365 connecté"); window.dispatchEvent(new Event("status:refresh")); reload(); }
        else if (n > 100) clearInterval(t);
      }, 3000);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const disconnect = async () => { if (!window.confirm("Déconnecter Microsoft 365 ? Les envois et la surveillance s'arrêteront.")) return; await api.post("/microsoft/disconnect"); reload(); };
  const test = async () => { try { await api.post("/microsoft/test-email", { to, confirm: true }); toast.success("Email de test envoyé"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Section title="1 · Application Azure (Entra ID)" description="Identifiants de l'application enregistrée dans votre tenant Microsoft 365.">
        <div className="space-y-4">
          <SecretField name="ms_tenant_id" label="Tenant ID (Directory ID)" status={s.secrets.ms_tenant_id} onSaved={reload} placeholder="ex. 1a2b3c4d-…" />
          <SecretField name="ms_client_id" label="Client ID (Application ID)" status={s.secrets.ms_client_id} onSaved={reload} />
          <SecretField name="ms_client_secret" label="Client Secret (valeur)" status={s.secrets.ms_client_secret} onSaved={reload} />
          <div className="space-y-1"><Label className="text-xs">URI de redirection à déclarer (plateforme « Web »)</Label>
            <div className="flex gap-2"><Input readOnly value={s.ms_redirect_uri} className="font-mono text-xs" data-testid="ms-redirect-uri" /><Button variant="outline" onClick={() => { navigator.clipboard.writeText(s.ms_redirect_uri); toast.success("Copié"); }}><Copy className="h-4 w-4" /></Button></div>
          </div>
          <ol className="text-xs text-slate-600 list-decimal ml-4 space-y-1">
            <li>portal.azure.com → Microsoft Entra ID → Inscriptions d'applications → Nouvelle inscription (comptes de cet annuaire uniquement).</li>
            <li>Authentification → Ajouter une plateforme « Web » → coller l'URI ci-dessus.</li>
            <li>Certificats et secrets → Nouveau secret client → copier la <b>valeur</b>.</li>
            <li>Autorisations API → Microsoft Graph → Délégué : <span className="font-mono">offline_access, User.Read, Mail.Send, Mail.ReadWrite</span> → Accorder le consentement administrateur.</li>
          </ol>
        </div>
      </Section>
      <div className="space-y-6">
        <Section title="2 · Connexion OAuth 2.0" description="Les jetons sont chiffrés côté serveur et ne transitent jamais par le navigateur.">
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm" data-testid="ms-status">
              {connected ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-slate-400" />}
              {connected ? <>Connecté : <b>{s.microsoft.account?.email}</b> <span className="text-slate-400 text-xs">depuis {fmtDate(s.microsoft.account?.connected_at)}</span></> : "Non connecté"}
            </div>
            <div className="flex gap-2">
              <Button onClick={connect} disabled={!s.secrets.ms_client_id?.set || !s.secrets.ms_client_secret?.set} data-testid="ms-connect-btn">{connected ? "Reconnecter" : "Connecter Microsoft 365"}</Button>
              {connected && <Button variant="outline" onClick={disconnect} data-testid="ms-disconnect-btn">Déconnecter</Button>}
              {connected && <TestButton name="microsoft" />}
            </div>
          </div>
        </Section>
        <Section title="3 · Email de test" description="Envoi unique, uniquement après votre autorisation.">
          <div className="flex gap-2"><Input type="email" placeholder="destinataire@exemple.com" value={to} onChange={(e) => setTo(e.target.value)} data-testid="ms-test-to" /><Button disabled={!connected || !to} onClick={test} data-testid="ms-test-send">Envoyer le test</Button></div>
        </Section>
        <Section title="Limites Microsoft 365">
          <ul className="text-xs text-slate-600 space-y-1 list-disc ml-4">
            <li>Exchange Online : 10 000 destinataires / 24 h et 30 messages / minute par boîte. L'application reste volontairement très en deçà (plafond configurable, 300 max).</li>
            <li>Toute erreur de limitation (429, quota, compte restreint, spam sortant) suspend immédiatement tous les envois et crée une alerte critique. Aucune tentative de contournement.</li>
          </ul>
        </Section>
      </div>
    </div>
  );
}

function Sending({ s, reload }) {
  const [v, setV] = useState(s.settings.sending);
  const [fu, setFu] = useState(s.settings.followups);
  const rt = s.runtime || {};
  const save = async () => {
    try { await api.put("/settings/sending", v); await api.put("/settings/followups", fu); toast.success("Paramètres d'envoi enregistrés"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const pause = async (paused) => { await api.post("/sending/global-pause", { paused, reason: paused ? "Arrêt d'urgence manuel" : "" }); toast.success(paused ? "Tous les envois sont suspendus" : "Envois réautorisés"); window.dispatchEvent(new Event("status:refresh")); reload(); };
  return (
    <div className="space-y-6">
      <Section title="Arrêt d'urgence" description="Suspend instantanément tous les envois et relances, toutes campagnes confondues.">
        <div className="flex items-center justify-between">
          <div className="text-sm" data-testid="global-pause-status">{rt.global_pause ? <span className="text-red-700 flex items-center gap-2"><OctagonPause className="h-4 w-4" />Envois suspendus — {rt.global_pause_reason} ({fmtDate(rt.global_pause_at)})</span> : <span className="text-emerald-700">Envois autorisés</span>}</div>
          {rt.global_pause ? <Button onClick={() => pause(false)} data-testid="global-resume-btn"><Play className="h-4 w-4 mr-1" />Réautoriser les envois</Button> : <Button variant="destructive" onClick={() => pause(true)} data-testid="global-pause-btn"><OctagonPause className="h-4 w-4 mr-1" />Tout suspendre</Button>}
        </div>
      </Section>
      <Section title="Cadence par défaut des nouvelles campagnes">
        <ScheduleEditor value={v} onChange={setV} cap={v.global_daily_cap} />
        <div className="mt-5 space-y-1 max-w-xs"><Label className="text-xs">Plafond global quotidien (toutes campagnes)</Label><Input type="number" min={1} max={300} value={v.global_daily_cap} onChange={(e) => setV({ ...v, global_daily_cap: Number(e.target.value) })} data-testid="global-daily-cap" /></div>
      </Section>
      <Section title="Relances par défaut">
        <div className="grid grid-cols-2 gap-3 max-w-md">
          <div className="space-y-1"><Label className="text-xs">Nombre de relances</Label><Input type="number" min={0} max={5} value={fu.count} onChange={(e) => setFu({ ...fu, count: Number(e.target.value) })} data-testid="default-followup-count" /></div>
          <div className="space-y-1"><Label className="text-xs">Délai (jours)</Label><Input type="number" min={1} value={fu.delay_days} onChange={(e) => setFu({ ...fu, delay_days: Number(e.target.value) })} data-testid="default-followup-delay" /></div>
        </div>
      </Section>
      <Button onClick={save} data-testid="sending-save">Enregistrer</Button>
    </div>
  );
}

function Research({ s, reload }) {
  const [v, setV] = useState(s.settings.research);
  const [titles, setTitles] = useState(s.settings.research.titles.join("\n"));
  const move = (i, d) => { const p = [...v.priority]; [p[i], p[i + d]] = [p[i + d], p[i]]; setV({ ...v, priority: p }); };
  const toggleProv = (p) => setV({ ...v, priority: v.priority.includes(p) ? v.priority.filter((x) => x !== p) : [...v.priority, p] });
  const save = async () => {
    try { await api.put("/settings/research", { ...v, titles: titles.split("\n").map((t) => t.trim()).filter(Boolean) }); toast.success("Paramètres de recherche enregistrés"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Section title="Fonctions recherchées" description="Une par ligne. Le classement de priorité reste : CEO → Founder/Co-Founder → President → CFO.">
        <Textarea rows={9} value={titles} onChange={(e) => setTitles(e.target.value)} className="font-mono text-xs" data-testid="research-titles" />
      </Section>
      <div className="space-y-6">
        <Section title="Priorité des fournisseurs">
          <div className="space-y-2">
            {["apollo", "rocketreach"].map((p) => (
              <div key={p} className="flex items-center gap-3 text-sm"><Switch checked={v.priority.includes(p)} onCheckedChange={() => toggleProv(p)} data-testid={`prov-toggle-${p}`} />{p === "apollo" ? "Apollo.io" : "RocketReach"}</div>
            ))}
            <div className="mt-3 space-y-1">
              {v.priority.map((p, i) => (
                <div key={p} className="flex items-center gap-2 border border-slate-200 rounded px-3 py-2 text-sm"><span className="tabular text-xs text-slate-400">{i + 1}</span><span className="flex-1">{p}</span>
                  <button disabled={i === 0} onClick={() => move(i, -1)} data-testid={`prov-up-${p}`}><ArrowUp className="h-4 w-4 text-slate-500" /></button>
                  <button disabled={i === v.priority.length - 1} onClick={() => move(i, 1)}><ArrowDown className="h-4 w-4 text-slate-500" /></button></div>
              ))}
            </div>
          </div>
        </Section>
        <Section title="Fiabilité et crédits">
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-3"><Switch checked={v.accepted_statuses.includes("risky")} onCheckedChange={(c) => setV({ ...v, accepted_statuses: c ? [...v.accepted_statuses, "risky"] : v.accepted_statuses.filter((x) => x !== "risky") })} data-testid="accept-risky-toggle" />Accepter les emails « risqués » (catch-all / devinés) — déconseillé</div>
            <div className="space-y-1 max-w-xs"><Label className="text-xs">Plafond de crédits par session de recherche</Label><Input type="number" value={v.max_credits_per_run} onChange={(e) => setV({ ...v, max_credits_per_run: Number(e.target.value) })} data-testid="max-credits-input" /></div>
          </div>
        </Section>
        <Button onClick={save} data-testid="research-save">Enregistrer</Button>
      </div>
    </div>
  );
}

function Exclusions() {
  const [items, setItems] = useState([]);
  const [vals, setVals] = useState("");
  const [q, setQ] = useState("");
  const load = useCallback(() => api.get("/suppressions", { params: { q } }).then((r) => setItems(r.data)), [q]);
  useEffect(() => { load(); }, [load]);
  const add = async () => { const r = await api.post("/suppressions", { values: vals, reason: "manual" }); toast.success(`${r.data.added} entrées ajoutées`); setVals(""); load(); };
  const del = async (id) => { try { await api.delete(`/suppressions/${id}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="grid lg:grid-cols-[1fr_1.4fr] gap-6">
      <Section title="Ajouter à la liste d'exclusion" description="Emails ou domaines, un par ligne. Exclusion immédiate et globale (toutes campagnes).">
        <Textarea rows={8} value={vals} onChange={(e) => setVals(e.target.value)} placeholder={"ceo@exemple.com\nconcurrent.ch"} className="font-mono text-xs" data-testid="suppression-input" />
        <Button className="mt-3" disabled={!vals.trim()} onClick={add} data-testid="suppression-add-btn">Ajouter</Button>
      </Section>
      <Section title={`Exclusions & oppositions (${items.length})`} description="Désinscriptions, refus, rebonds, effacements et exclusions manuelles.">
        <Input placeholder="Rechercher…" value={q} onChange={(e) => setQ(e.target.value)} className="mb-3" data-testid="suppression-search" />
        <div className="max-h-[420px] overflow-y-auto divide-y divide-slate-100" data-testid="suppression-list">
          {items.map((s) => (
            <div key={s.id} className="flex items-center justify-between py-2 text-sm">
              <div><span className="font-mono text-xs">{s.value}</span> <span className="text-[11px] text-slate-400 ml-2">{s.type} · {s.reason} · {fmtDate(s.created_at)}</span></div>
              {!["unsubscribe", "unsubscribe_link", "erasure_request"].includes(s.reason) && <button onClick={() => del(s.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

const ICON = { ok: <CheckCircle2 className="h-4 w-4 text-emerald-600" />, warning: <AlertTriangle className="h-4 w-4 text-amber-600" />, error: <XCircle className="h-4 w-4 text-red-600" /> };

function Deliverability({ s, reload }) {
  const [v, setV] = useState(s.settings.tracking);
  const [comp, setComp] = useState(s.settings.compliance);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const check = async () => { setBusy(true); try { setRes((await api.get("/deliverability/check")).data); } catch (e) { toast.error(errMsg(e)); } setBusy(false); };
  const save = async () => { try { await api.put("/settings/tracking", v); await api.put("/settings/compliance", comp); toast.success("Enregistré"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-6">
      <Section title="Vérification DNS (lecture seule)" description="Aucun enregistrement n'est modifié automatiquement. Chaque vérification enregistre une sauvegarde de l'état DNS actuel."
        actions={<Button size="sm" onClick={check} disabled={busy} data-testid="dns-check-btn">{busy ? "Analyse…" : "Analyser"}</Button>}>
        {!res ? <div className="text-sm text-slate-500">Lancez l'analyse pour contrôler SPF, DKIM, DMARC, MX et le sous-domaine de tracking.</div> : (
          <div className="divide-y divide-slate-100" data-testid="dns-results">
            {res.checks.map((c) => (
              <div key={c.key} className="py-3 grid grid-cols-[24px_180px_1fr] gap-3 text-sm">
                {ICON[c.status]}<div className="font-medium">{c.label}</div>
                <div><div className="font-mono text-xs break-all text-slate-700">{c.value}</div>{c.advice && <div className="text-xs text-slate-500 mt-1">{c.advice}</div>}</div>
              </div>
            ))}
            <div className="pt-3 text-xs text-slate-500">Sauvegarde DNS #{res.snapshot_id.slice(0, 8)} · {fmtDate(res.checked_at)}</div>
          </div>
        )}
      </Section>
      <div className="grid lg:grid-cols-2 gap-6">
        <Section title="Tracking et sous-domaine">
          <div className="space-y-3">
            {[["domain", "Domaine d'envoi"], ["subdomain", "Sous-domaine de tracking"], ["base_url", "URL de base des liens (vide = URL de l'application)"]].map(([k, l]) => (
              <div key={k} className="space-y-1"><Label className="text-xs">{l}</Label><Input value={v[k] || ""} onChange={(e) => setV({ ...v, [k]: e.target.value })} data-testid={`tracking-${k}`} /></div>
            ))}
            <div className="space-y-1"><Label className="text-xs">Sélecteurs DKIM (séparés par des virgules)</Label><Input value={(v.dkim_selectors || []).join(",")} onChange={(e) => setV({ ...v, dkim_selectors: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} /></div>
            <div className="flex items-center gap-3 text-sm"><Switch checked={v.track_opens} onCheckedChange={(c) => setV({ ...v, track_opens: c })} data-testid="track-opens-toggle" />Suivi des ouvertures (estimation)</div>
            <div className="flex items-center gap-3 text-sm"><Switch checked={v.track_clicks} onCheckedChange={(c) => setV({ ...v, track_clicks: c })} data-testid="track-clicks-toggle" />Suivi des clics</div>
            <p className="text-xs text-slate-500">URL active des liens : <span className="font-mono">{s.tracking_base}</span>. Pour utiliser {v.subdomain}, créez dans GoDaddy un CNAME vers l'application déployée et rattachez le domaine personnalisé, puis renseignez https://{v.subdomain} ci-dessus.</p>
          </div>
        </Section>
        <Section title="Conformité des emails">
          <div className="space-y-3">
            <div className="space-y-1"><Label className="text-xs">Pied de page de désinscription ({"{unsubscribe_link}"} obligatoire)</Label><Textarea rows={3} value={comp.footer_text} onChange={(e) => setComp({ ...comp, footer_text: e.target.value })} data-testid="footer-text" /></div>
            <div className="space-y-1"><Label className="text-xs">Texte du lien cliquable (remplace {"{unsubscribe_link}"})</Label><Input value={comp.unsubscribe_label || ""} onChange={(e) => setComp({ ...comp, unsubscribe_label: e.target.value })} placeholder="Se désinscrire" data-testid="unsubscribe-label" /></div>
            <div className="space-y-1"><Label className="text-xs">Identité de l'expéditeur</Label><Input value={comp.sender_identity} onChange={(e) => setComp({ ...comp, sender_identity: e.target.value })} data-testid="sender-identity" /></div>
            <div className="text-xs text-slate-500 border-t border-slate-100 pt-3">Tests de placement inbox/spam : utilisez un service spécialisé (GlockApps, mail-tester.com) en envoyant l'email de test de campagne à l'adresse fournie par le service. Les résultats sont des <b>estimations</b>.</div>
          </div>
        </Section>
      </div>
      <Button onClick={save} disabled={!comp.footer_text.includes("{unsubscribe_link}")} data-testid="deliverability-save">Enregistrer</Button>
    </div>
  );
}

function Security() {
  const [pwd, setPwd] = useState({ current_password: "", new_password: "" });
  const [logs, setLogs] = useState([]);
  const [backups, setBackups] = useState([]);
  const load = () => { api.get("/audit").then((r) => setLogs(r.data)); api.get("/data/backups").then((r) => setBackups(r.data)); };
  useEffect(() => { load(); }, []);
  const change = async () => { try { await api.post("/auth/change-password", pwd); toast.success("Mot de passe modifié"); setPwd({ current_password: "", new_password: "" }); } catch (e) { toast.error(errMsg(e)); } };
  const backup = async () => { try { await api.post("/data/backups"); toast.success("Sauvegarde en cours de création — elle apparaîtra dans la liste d'ici une minute"); setTimeout(load, 15000); setTimeout(load, 45000); } catch (e) { toast.error(errMsg(e)); } };
  const exportAll = async () => { try { await api.get("/data/export"); toast.success("Export en préparation — téléchargez-le dans « Sauvegardes » d'ici une minute"); setTimeout(load, 15000); setTimeout(load, 45000); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-6">
      <div className="grid lg:grid-cols-3 gap-6">
        <Section title="Mot de passe">
          <div className="space-y-3">
            <Input type="password" placeholder="Mot de passe actuel" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} data-testid="pwd-current" />
            <Input type="password" placeholder="Nouveau (12 caractères min.)" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} data-testid="pwd-new" />
            <Button onClick={change} data-testid="pwd-save">Modifier</Button>
          </div>
        </Section>
        <Section title="Export des données" description="Export complet JSON (droit d'accès / portabilité), préparé en arrière-plan puis disponible dans Sauvegardes.">
          <Button variant="outline" onClick={exportAll} data-testid="data-export-btn">Exporter toutes les données</Button>
        </Section>
        <Section title="Sauvegardes" description="Automatiques toutes les 24 h (14 conservées)." actions={<Button size="sm" variant="outline" onClick={backup} data-testid="backup-now-btn">Sauvegarder</Button>}>
          <div className="space-y-1 text-xs max-h-40 overflow-y-auto" data-testid="backups-list">
            {backups.length === 0 ? <div className="text-slate-500">Aucune sauvegarde.</div> : backups.map((b) => (
              <button key={b.name} onClick={() => download(`/data/backups/${b.name}`, b.name)} className="block w-full text-left hover:underline font-mono">{b.name} · {(b.size / 1024).toFixed(0)} Ko</button>
            ))}
          </div>
        </Section>
      </div>
      <Section title="Journal d'audit" description="200 dernières opérations">
        <div className="max-h-[420px] overflow-y-auto text-xs divide-y divide-slate-100" data-testid="audit-log">
          {logs.map((l) => (
            <div key={l.id} className="py-2 grid grid-cols-[140px_200px_160px_1fr] gap-3">
              <span className="text-slate-500">{fmtDate(l.created_at)}</span><span className="font-mono">{l.action}</span><span className="text-slate-500 truncate">{l.actor}</span>
              <span className="text-slate-600 truncate font-mono">{JSON.stringify(l.details)}</span>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

export default function Settings() {
  const [s, setS] = useState(null);
  const [params] = useSearchParams();
  const load = useCallback(() => api.get("/settings").then((r) => setS(r.data)), []);
  useEffect(() => {
    load();
    if (params.get("ms") === "connected") toast.success("Microsoft 365 connecté", { id: "ms-status" });
    if (params.get("ms") === "error") toast.error(`Connexion Microsoft échouée : ${params.get("msg") || ""}`, { id: "ms-status" });
    if (params.get("ms")) window.history.replaceState(null, "", "/settings");
  }, [load, params]);
  if (!s) return <div className="text-sm text-slate-500">Chargement…</div>;
  return (
    <div>
      <PageHeader eyebrow="Configuration" title="Paramètres" description="Toutes les clés et jetons sont chiffrés côté serveur. Aucune clé secrète n'est exposée au navigateur." />
      <Tabs defaultValue={params.get("ms") ? "microsoft" : "integrations"}>
        <TabsList className="mb-6 flex-wrap h-auto">
          {[["integrations", "Intégrations API"], ["microsoft", "Microsoft 365"], ["sending", "Envoi & quotas"], ["research", "Recherche"], ["exclusions", "Exclusions"], ["deliverability", "Délivrabilité"], ["security", "Sécurité & données"]].map(([k, l]) => (
            <TabsTrigger key={k} value={k} data-testid={`stab-${k}`}>{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="integrations"><Integrations s={s} reload={load} /></TabsContent>
        <TabsContent value="microsoft"><Microsoft s={s} reload={load} /></TabsContent>
        <TabsContent value="sending"><Sending s={s} reload={load} /></TabsContent>
        <TabsContent value="research"><Research s={s} reload={load} /></TabsContent>
        <TabsContent value="exclusions"><Exclusions /></TabsContent>
        <TabsContent value="deliverability"><Deliverability s={s} reload={load} /></TabsContent>
        <TabsContent value="security"><Security /></TabsContent>
      </Tabs>
    </div>
  );
}
